/**
 * Whitelabel feature flag utilities.
 *
 * Reads from window.__WHITELABEL__ injected by whitelabel-runtime.js
 * (generated at build time by scripts/whitelabel-prebuild.js).
 */
import { APP_TYPE, ROOM_MODEL } from './constants';
import { NativeModules, Platform } from 'react-native';
import { getPersistentJson } from '../../vrs-auth/storage';
import malkaConfig from '../../../../whitelabel/malka.json';
import malkaRelayConfig from '../../../../whitelabel/malkarelay.json';
import malkaTerpConfig from '../../../../whitelabel/malkaterp.json';
import malkaStagingConfig from '../../../../whitelabel/malka-staging.json';
import malkaVcsConfig from '../../../../whitelabel/malkavcs.json';
import malkaVriConfig from '../../../../whitelabel/malkavri.json';
import malkaVriStagingConfig from '../../../../whitelabel/malkavri-staging.json';
import mapleConfig from '../../../../whitelabel/maple.json';
import mapleStagingConfig from '../../../../whitelabel/maple-staging.json';
const STATIC_TENANTS = {
    // The legacy interpreter name remains a release-script alias only. Runtime behavior is
    // canonicalized to the same Malka Terp config as the dedicated bundle.
    interpreter: malkaTerpConfig,
    malka: malkaConfig,
    malkarelay: malkaRelayConfig,
    malkaterp: malkaTerpConfig,
    'malka-staging': malkaStagingConfig,
    malkavcs: malkaVcsConfig,
    malkavri: malkaVriConfig,
    'malkavri-staging': malkaVriStagingConfig,
    maple: mapleConfig,
    'maple-staging': mapleStagingConfig
};
function isRecord(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
/**
 * The bundled tenant JSON for a server tenant id, or undefined for a tenant
 * this build does not ship. Malka Terp serves every tenant's interpreters
 * from one build, so screens that wear the signed-in interpreter's own
 * tenant (Jobs branding) read it by id rather than from the build.
 */
export function getBundledTenantConfig(tenantId) {
    return tenantId && Object.prototype.hasOwnProperty.call(STATIC_TENANTS, tenantId)
        ? STATIC_TENANTS[tenantId] : undefined;
}
function getGlobalWhitelabel() {
    try {
        const globalScope = typeof globalThis !== 'undefined'
            ? globalThis
            : undefined;
        return normalizeConfig(globalScope?.window?.__WHITELABEL__ || globalScope?.__WHITELABEL__);
    }
    catch {
        return undefined;
    }
}
/**
 * The tenant key of this build itself (`malka-staging`, not the `malka` that
 * its file names as tenantId), or undefined when the build has none. A
 * per-build flag reads the build's own file by it (tablet/tabletFlag.ts).
 *
 * @returns {string|undefined}
 */
export function getBuildTenantKey() {
    return getBuildTenantId();
}
function getBuildTenantId() {
    try {
        // A dedicated native bundle is the strongest identity signal we have.
        // Do not let stale generic build variables override a dedicated app.
        const nativeTenantId = getNativeTenantId();
        if (nativeTenantId) {
            return nativeTenantId;
        }
        const globalScope = typeof globalThis !== 'undefined'
            ? globalThis
            : undefined;
        const env = globalScope?.process?.env;
        return env?.TENANT
            || env?.VRS_TENANT
            || env?.EXPO_PUBLIC_TENANT
            || getBrowserTenantId();
    }
    catch {
        return getNativeTenantId() || getBrowserTenantId();
    }
}
function getBrowserTenantId() {
    if (typeof window === 'undefined') {
        return undefined;
    }
    const hostnameValue = window.location?.hostname;
    if (typeof hostnameValue !== 'string' || !hostnameValue) {
        return undefined;
    }
    const hostname = hostnameValue.toLowerCase();
    if (hostname === 'relay.malkacomm.com') {
        return 'malkarelay';
    }
    if (hostname === 'relay.maplecomm.ca') {
        return 'malkarelay';
    }
    if (hostname === 'app.malkacomm.com') {
        return 'malkavcs';
    }
    if (hostname === 'vrs.malkacomm.com') {
        return 'malka';
    }
    if (hostname === 'staging-vri.maplecomm.ca') {
        return 'maple-staging';
    }
    if (hostname === 'vri.maplecomm.ca' || hostname.endsWith('.maplecomm.ca')) {
        return 'maple';
    }
    if (hostname === 'vri.malkacomm.com') {
        return 'malkavri';
    }
    if (hostname === 'staging-vri.malkacomm.com') {
        return 'malkavri-staging';
    }
    return undefined;
}
function qualifyBrowserAssetPath(value) {
    if (Array.isArray(value)) {
        return value.map(qualifyBrowserAssetPath);
    }
    if (isRecord(value)) {
        return Object.fromEntries(Object.entries(value).map(([key, nestedValue]) => [key, qualifyBrowserAssetPath(nestedValue)]));
    }
    if (typeof value !== 'string' || !value) {
        return value;
    }
    if (/^(https?:)?\/\//.test(value) || value.startsWith('/') || value.startsWith('data:') || value.startsWith('static/')) {
        return value;
    }
    return `images/${value}`;
}
function getBrowserHostConfigOverride() {
    const tenantId = getBrowserTenantId();
    if (!tenantId) {
        return undefined;
    }
    const config = normalizeConfig(STATIC_TENANTS[tenantId]);
    if (!config) {
        return undefined;
    }
    return {
        ...config,
        assets: qualifyBrowserAssetPath(config.assets || {})
    };
}
export function isNativeRuntime() {
    if (Platform.OS !== 'web') {
        return true;
    }
    return typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
}
function getNativeTenantId() {
    if (!isNativeRuntime()) {
        return undefined;
    }
    const appInfo = (NativeModules?.AppInfo || {});
    const bundleIdentifier = String(appInfo.bundleIdentifier || '').toLowerCase();
    if (bundleIdentifier === 'com.malkacomm.relay') {
        return 'malkarelay';
    }
    if (bundleIdentifier === 'com.malkacomm.terp') {
        return 'malkaterp';
    }
    if (bundleIdentifier === 'com.maplecomm.vri') {
        return 'maple';
    }
    if (bundleIdentifier === 'com.malkacomm.vri') {
        return 'malkavri';
    }
    if (bundleIdentifier === 'com.malkacomm.vrs') {
        return 'malka';
    }
    const nativeTenantId = String(appInfo.tenantId || '').toLowerCase();
    if (STATIC_TENANTS[nativeTenantId]) {
        return nativeTenantId;
    }
    const appName = String(appInfo.name || '').toLowerCase();
    if (appName.includes('terp') || appName.includes('interpreter')) {
        return appName.includes('terp') ? 'malkaterp' : 'interpreter';
    }
    if (appName.includes('maple')) {
        return 'maple';
    }
    if (appName.includes('relay')) {
        return 'malkarelay';
    }
    if (appName.includes('vri')) {
        return 'malkavri';
    }
    if (appName.includes('vrs') || appName.includes('malka')) {
        return 'malka';
    }
    return undefined;
}
/**
 * Flatten nested `{ enabled: … }` feature blocks into plain values.
 *
 * `scripts/whitelabel-prebuild.js` performs the same flattening when it
 * generates `whitelabel-runtime.js`, which is the copy the browser actually
 * receives. The two must stay in step: a key handled here but not there is
 * simply absent in production, with no error to show for it.
 */
function flattenFeatures(features = {}) {
    const flattened = {};
    for (const [key, value] of Object.entries(features)) {
        if (key === 'languages' && isRecord(value)) {
            flattened.languages = value.enabled;
            flattened.defaultLanguage = value.default;
            flattened.languageQuickSwitch = value.quickSwitch === true;
        }
        else if (isRecord(value) && 'enabled' in value) {
            flattened[key] = value.enabled;
        }
        else {
            flattened[key] = value;
        }
    }
    return flattened;
}
function normalizeConfig(config) {
    if (!isRecord(config)) {
        return undefined;
    }
    if (typeof config.appName === 'string') {
        return config;
    }
    const brand = isRecord(config.brand) ? config.brand : {};
    return {
        tenantId: typeof config.tenantId === 'string' ? config.tenantId : undefined,
        appName: typeof brand.appName === 'string' ? brand.appName : undefined,
        providerName: typeof brand.providerName === 'string' ? brand.providerName : undefined,
        tagline: typeof brand.tagline === 'string' ? brand.tagline : undefined,
        description: typeof brand.description === 'string' ? brand.description : undefined,
        supportUrl: typeof brand.supportUrl === 'string' ? brand.supportUrl : undefined,
        domains: isRecord(config.domains) ? config.domains : {},
        theme: isRecord(config.theme) ? config.theme : {},
        features: flattenFeatures(isRecord(config.features) ? config.features : {}),
        assets: isRecord(config.assets) ? config.assets : {},
        operations: isRecord(config.operations) ? config.operations : {}
    };
}
function getStaticTenantConfig() {
    const tenantId = getBuildTenantId()
        || getPersistentJson('vrs_tenant_config')?.tenantId
        || getPersistentJson('vrs_user_info')?.tenantId
        || 'malka';
    return normalizeConfig(STATIC_TENANTS[tenantId] || STATIC_TENANTS.malka) || {};
}
function getCachedWhitelabelConfig() {
    const cached = normalizeConfig(getPersistentJson('vrs_tenant_config'));
    const buildTenantId = getBuildTenantId();
    if (buildTenantId && cached?.tenantId && cached.tenantId !== buildTenantId) {
        return undefined;
    }
    return cached;
}
/**
 * Get the full whitelabel config object.
 */
export function getWhitelabelConfig() {
    const buildTenantId = getBuildTenantId();
    const browserConfig = getBrowserHostConfigOverride();
    const globalConfig = normalizeConfig(getGlobalWhitelabel());
    if (isNativeRuntime() && buildTenantId) {
        const staticConfig = normalizeConfig(STATIC_TENANTS[buildTenantId]);
        const cachedConfig = getCachedWhitelabelConfig();
        const matchingGlobalConfig = globalConfig?.tenantId === buildTenantId ? globalConfig : undefined;
        const dynamicConfig = matchingGlobalConfig || cachedConfig;
        if (staticConfig) {
            // The theme comes from this build. The copy cached at sign-in is
            // from the build that signed in, so a colour fix in a new build
            // must not wait for a sign-out (MapleVRI 2026-10-13: #666666 text
            // on #666666 stayed in the cache).
            return dynamicConfig
                ? {
                    ...staticConfig,
                    ...dynamicConfig,
                    appName: staticConfig.appName,
                    theme: staticConfig.theme,
                    operations: {
                        ...staticConfig.operations,
                        ...dynamicConfig.operations,
                        appType: staticConfig.operations?.appType
                    },
                    tenantId: staticConfig.tenantId
                }
                : staticConfig;
        }
    }
    if (browserConfig) {
        return {
            ...globalConfig,
            ...browserConfig
        };
    }
    return globalConfig
        || (buildTenantId ? normalizeConfig(STATIC_TENANTS[buildTenantId]) : undefined)
        || getCachedWhitelabelConfig()
        || getStaticTenantConfig();
}
/**
 * Check whether a specific feature is enabled for the current tenant.
 *
 * Returns true (enabled) by default if no whitelabel config is present
 * or if the feature key is not defined in the config.
 */
export function isFeatureEnabled(feature) {
    const wl = getWhitelabelConfig();
    if (!wl?.features) {
        return true; // default: all features enabled
    }
    const val = wl.features[feature];
    if (typeof val === 'boolean') {
        return val;
    }
    return true; // unknown features default to enabled
}
export { isCertifiedIpCtsEnabled } from './certifiedMode';
/**
 * Get the list of languages enabled for the current tenant.
 *
 * `flattenFeatures` reduces `features.languages` to the bare enabled array, so
 * that is the shape seen at runtime; the object form is accepted too for raw
 * tenant JSON passed straight in. Falls back to a sensible default if no config
 * is present.
 */
export function getEnabledLanguages() {
    const wl = getWhitelabelConfig();
    const languages = wl?.features?.languages;
    if (Array.isArray(languages)) {
        return languages.filter((language) => typeof language === 'string');
    }
    if (isRecord(languages) && Array.isArray(languages.enabled)) {
        return languages.enabled.filter((language) => typeof language === 'string');
    }
    return ['en', 'asl', 'fr', 'lsq'];
}
/**
 * Get the default language for the current tenant.
 */
export function getDefaultLanguage() {
    const wl = getWhitelabelConfig();
    const defaultLanguage = wl?.features?.defaultLanguage;
    if (typeof defaultLanguage === 'string' && defaultLanguage) {
        return defaultLanguage;
    }
    const languages = wl?.features?.languages;
    if (isRecord(languages) && typeof languages.default === 'string' && languages.default) {
        return languages.default;
    }
    return 'en';
}
/**
 * Whether this tenant wants the always-visible language toggle.
 *
 * Opt-in per tenant (`features.languages.quickSwitch`) rather than inferred
 * from the enabled list: MalkaVRS enables fifteen languages but wants them in
 * the settings dialog, while MalkaVCS wants a one-tap English/Arabic switch on
 * screen at all times.
 */
export function isLanguageQuickSwitchEnabled() {
    const features = getWhitelabelConfig()?.features;
    if (features?.languageQuickSwitch === true) {
        return true;
    }
    const languages = features?.languages;
    return isRecord(languages) && languages.quickSwitch === true;
}
/**
 * Get the tenant's app name.
 */
export function getAppName() {
    const wl = getWhitelabelConfig();
    if (wl?.appName) {
        return wl.appName;
    }
    return 'MalkaVRS';
}
/**
 * Get the tenant's service tagline, e.g. "Video Communication Service".
 */
export function getTagline() {
    const wl = getWhitelabelConfig();
    if (typeof wl?.tagline === 'string' && wl.tagline) {
        return wl.tagline;
    }
    return 'Video Relay Service';
}
/**
 * Split the tenant app name into its provider prefix and service suffix, so a
 * two-tone wordmark ("Malka" + "VCS") follows the tenant instead of being
 * hardcoded per screen. Falls back to the whole name as the prefix.
 */
export function getBrandWordmarkParts() {
    const appName = getAppName();
    const match = (/^(.*?)(VRS|VRI|VCS|Relay|Terp)$/i).exec(appName);
    if (match?.[1]) {
        return { prefix: match[1], suffix: match[2] };
    }
    return { prefix: appName, suffix: '' };
}
/**
 * The tenant's short brand for running copy ("Join my Maple call"): the
 * wordmark prefix, so MapleVRI reads "Maple" and MalkaVRS "Malka".
 */
export function getBrandShortName() {
    return getBrandWordmarkParts().prefix.trim() || getAppName();
}
/**
 * Get the tenant's logo URL (white variant for dark backgrounds).
 */
export function getLogoWhiteUrl() {
    const wl = getWhitelabelConfig();
    const logoWhite = wl?.assets?.logoWhite;
    if (typeof logoWhite === 'string' && logoWhite) {
        return logoWhite;
    }
    return 'images/malka-logo-white.png';
}
/**
 * Get the tenant's primary logo URL.
 */
export function getLogoUrl() {
    const wl = getWhitelabelConfig();
    const logo = wl?.assets?.logo;
    if (typeof logo === 'string' && logo) {
        return logo;
    }
    return 'images/malka-logo.png';
}
/**
 * Check if the current tenant is not "malka" (i.e., is a whitelabeled tenant).
 */
export function isWhitelabeled() {
    const wl = getWhitelabelConfig();
    return wl ? wl.tenantId !== 'malka' : false;
}
/**
 * Get the current tenant ID.
 */
export function getTenantId() {
    return getWhitelabelConfig()?.tenantId ?? 'malka';
}
/**
 * Get the app type (VRS or VRI) for the current build.
 *
 * Determined by the `operations.appType` field in the tenant config.
 * Falls back to checking `features.vrs` vs `features.vri`.
 */
export function getAppType() {
    const wl = getWhitelabelConfig();
    // Explicit appType from tenant config
    if (wl?.operations?.appType === APP_TYPE.RELAY
        || wl?.operations?.appType === APP_TYPE.TERP
        || wl?.operations?.appType === APP_TYPE.VRS
        || wl?.operations?.appType === APP_TYPE.VRI
        || wl?.operations?.appType === APP_TYPE.INTERPRETER) {
        return wl.operations.appType;
    }
    // Fallback: infer from default service modes
    const modes = wl?.operations?.defaultServiceModes;
    if (Array.isArray(modes) && modes.includes('vrs')) {
        return APP_TYPE.VRS;
    }
    return APP_TYPE.VRI;
}
/**
 * Get the room model for the current tenant.
 *
 * Read from `operations.roomModel`. Tenants that do not declare one keep the
 * historical behaviour: VRS-shaped apps relay through a phone leg, everything
 * else puts both parties in the room.
 */
export function getRoomModel() {
    const wl = getWhitelabelConfig();
    const declared = wl?.operations?.roomModel;
    if (declared === ROOM_MODEL.RELAY || declared === ROOM_MODEL.UNIFIED) {
        return declared;
    }
    return getAppType() === APP_TYPE.VRS ? ROOM_MODEL.RELAY : ROOM_MODEL.UNIFIED;
}
/**
 * Whether both parties belong in the room and the interpreter should be
 * audible to everyone in it.
 */
export function isUnifiedRoomModel() {
    return getRoomModel() === ROOM_MODEL.UNIFIED;
}
/**
 * Whether this build is a VRS app (MalkaVRS).
 */
export function isVrsApp() {
    return getAppType() === APP_TYPE.VRS;
}
/**
 * Whether this build is a VRI app (MalkaVRI or MapleVRI).
 */
export function isVriApp() {
    return getAppType() === APP_TYPE.VRI;
}
/**
 * Whether this build is a Relay-style accessible calling app.
 */
export function isRelayApp() {
    return getAppType() === APP_TYPE.RELAY;
}
/**
 * Whether this build is the Malka Terp app (interpreters accept live
 * VRS/VRI calls and manage freelance Jobs). Inverts the client-app default:
 * the interpreter role is the intended user here, not the one turned away.
 */
export function isInterpreterApp() {
    return getAppType() === APP_TYPE.INTERPRETER;
}
/**
 * Whether this build is the legacy interpreter-side app lane (Malka Terp).
 */
export function isTerpApp() {
    return getAppType() === APP_TYPE.TERP;
}
